// Mirrors Api::V1::AnswersController::BULK_MAX_IDS: the server rejects larger requests.
export const BULK_ANSWERS_MAX_IDS = 500
const BULK_ANSWERS_CONCURRENCY = 3

export type BulkChunkResult<T> = {
    answers: T[]
    ok: boolean
    status: number | null
}

export function chunkIds(ids: string[], size = BULK_ANSWERS_MAX_IDS): string[][] {
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
    return chunks
}

// Fetches ids in server-sized chunks with limited concurrency. Answers keep chunk order;
// ok is false if any chunk failed (answers from the chunks that succeeded are kept).
export async function fetchAnswersInChunks<T>(
    ids: string[],
    fetchChunk: (chunk: string[]) => Promise<BulkChunkResult<T>>,
    concurrency = BULK_ANSWERS_CONCURRENCY,
): Promise<BulkChunkResult<T>> {
    const chunks = chunkIds(ids)
    if (chunks.length <= 1) return fetchChunk(ids)

    const results: BulkChunkResult<T>[] = new Array(chunks.length)
    let next = 0
    const worker = async () => {
        while (next < chunks.length) {
            const index = next++
            results[index] = await fetchChunk(chunks[index])
        }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, chunks.length) }, worker))

    const failed = results.find(r => !r.ok)
    return {
        answers: results.flatMap(r => r.answers),
        ok: !failed,
        status: failed ? failed.status : results[0].status,
    }
}
